// What your whole economy adds up to.
//
// The pure model behind the readouts' stock pill and the breakdown it opens
// (read through `useEconomy`; it was the dock's nothing-selected view before
// U8, and the name is older than the surface). Kept out of the component for
// the same reason `pieceBadges` is: these are numbers a player makes decisions
// on, and a number is worth a test.
//
// Three places material can be, and it is worth naming them rather than
// summing them into one figure: **in your pieces** is what you can spend now,
// **on your belts** is what is on the way, and **in the ground** is what you
// have not dug yet. A single total would hide the difference between a
// factory that is working and one that is merely rich.

import { type Color, pieceColor, pieceType } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import {
  beltOwnerAt,
  blueprintOwnerAt,
  droppedAt,
  cookingCount,
  cookingUnitAt,
  hasBelt,
  type IndustryState,
  mineableAt,
  liveSquares,
  structureAt,
  structureSquares,
  type Store,
  unitAt,
  unitCount,
  GRADE_SLOTS,
  unitGrade,
  unitLine
} from "src/engine/rules/industry/state";
import { isHarvesterAt } from "src/engine/rules/industry/machines";
import {
  LINE_COUNT,
  materialName,
  type MaterialLine
} from "src/engine/rules/materials";

/**
 * A bucket's index: **line first, then rung** (`line * GRADE_SLOTS + grade`).
 *
 * The buckets used to be grade-indexed and pooled every line into one, so a
 * piece holding six cobble and two iron read as "8 Iron" — reported from a run
 * as harvesting no stone at all, when the stone was in hand and named wrong.
 *
 * A slot rather than an array of arrays, so `totalUnits` is still one sum and
 * every existing reading of `held[0]` still means metal at grade 0 — metal is
 * line nought, so its slots are unmoved. `unitName` is the one place a slot
 * becomes words.
 */
export const unitSlot = (unit: number): number =>
  unitLine(unit) * GRADE_SLOTS + unitGrade(unit);

/** What a slot is called — Iron, Cobble, Electrum. */
export const slotName = (slot: number): string =>
  materialName(Math.floor(slot / GRADE_SLOTS) as MaterialLine, slot % GRADE_SLOTS);

/** How many buckets a per-slot array needs. */
export const UNIT_SLOTS = LINE_COUNT * GRADE_SLOTS;

/**
 * What one square is holding, dearest slot first — swatch counts for a badge
 * or a card.
 *
 * **Per unit and by slot**, because a rung is counted across every line and a
 * pawn holding cobble was reporting iron. Artifacts are left out: they are not
 * material and have no slot to fall in.
 */
export function slotCounts(
  industry: IndustryState,
  sq: number,
  stores: readonly Store[]
): { slot: number; count: number }[] {
  const bySlot = new Map<number, number>();
  for (const store of stores) {
    for (let i = 0; i < unitCount(industry, sq, store); i++) {
      const unit = unitAt(industry, sq, i, store);
      bySlot.set(unitSlot(unit), (bySlot.get(unitSlot(unit)) ?? 0) + 1);
    }
  }
  return [...bySlot]
    .map(([slot, count]) => ({ slot, count }))
    .sort((a, b) => b.slot - a.slot);
}

export interface ShelfTotals {
  /** Units by `unitSlot`, held inside your pieces. */
  held: number[];
  /** Units by `unitSlot` riding your belts. */
  onBelts: number[];
  /** Scrap still in heaps that one of your harvesters is standing on. */
  underfoot: number;
  /** Belt parts you own. */
  belts: number;
  /**
   * Parts you have drawn that are still waiting for their iron.
   *
   * Its own number rather than folded into `belts`, because they are two
   * different facts about the factory: one is track that carries, the other is
   * a bill outstanding. A run that never gets built is a run nothing is
   * supplying, and this is the only place that says so.
   */
  drawn: number;
  /** Units in your warehouses by `unitSlot`, and how many there are. */
  stored: number[];
  stores: number;
  /** Pieces of yours holding anything at all. */
  loaded: number;
  /** Units in ovens right now — material that is busy rather than spendable. */
  cooking: number;
  /**
   * Loose metal lying on the floor, by `unitSlot`.
   *
   * Its own bucket: it is neither in your hands nor on a lane, and it belongs
   * to nobody until somebody walks onto it — enemies included. Which is what
   * makes it worth saying rather than folding into `held`.
   */
  looseMetal: number[];
  /**
   * How many of each piece type you have, indexed by `PieceType`.
   *
   * On the classic board you can count your army by looking at it. On a board
   * that runs past the viewport and grows every stage you cannot, and "what do
   * I actually still have" is the question behind every decision to trade.
   */
  pieces: number[];
}

const empty = (): number[] => new Array<number>(UNIT_SLOTS).fill(0);

export function shelfTotals(state: GameState, side: Color): ShelfTotals {
  const totals: ShelfTotals = {
    held: empty(),
    onBelts: empty(),
    underfoot: 0,
    belts: 0,
    drawn: 0,
    stored: empty(),
    stores: 0,
    loaded: 0,
    cooking: 0,
    looseMetal: empty(),
    // Indexed by piece type, which runs 1..6; index 0 is never used.
    pieces: new Array<number>(7).fill(0)
  };

  for (const [sq, piece] of state.position.board) {
    if (pieceColor(piece) !== side) continue;
    totals.pieces[pieceType(piece)]++;

    let carrying = 0;
    for (const store of ["in", "out"] as const) {
      for (let i = 0; i < unitCount(state.industry, sq, store); i++) {
        const unit = unitAt(state.industry, sq, i, store);
        totals.held[unitSlot(unit)]++;
        carrying++;
      }
    }

    const inOven = cookingCount(state.industry, sq);
    for (let i = 0; i < inOven; i++) {
      const unit = cookingUnitAt(state.industry, sq, i);
      totals.held[unitSlot(unit)]++;
      totals.cooking++;
      carrying++;
    }

    if (carrying > 0) totals.loaded++;
    // Only where one of yours could actually work it. Scrap on the far side of
    // the board is not your stock, it is the map.
    if (isHarvesterAt(state.position, sq)) totals.underfoot += mineableAt(state.industry, sq);
  }

  // Sorted, so two clients — and a game and its replay — name them in the
  // same order.
  for (const sq of liveSquares(state.industry.dropped)) {
    for (const unit of droppedAt(state.industry, sq)) totals.looseMetal[unitSlot(unit)]++;
  }

  for (const sq of liveSquares(state.industry.blueprintDir)) {
    if (blueprintOwnerAt(state.industry, sq) === side) totals.drawn++;
  }

  for (const sq of liveSquares(state.industry.beltDir)) {
    if (!hasBelt(state.industry, sq) || beltOwnerAt(state.industry, sq) !== side) continue;
    totals.belts++;
    // Per unit rather than `cargoAt`'s per-grade count: a lane can carry two
    // lines at once and a count keyed by rung alone cannot say which.
    for (let i = 0; i < unitCount(state.industry, sq, "cargo"); i++) {
      totals.onBelts[unitSlot(unitAt(state.industry, sq, i, "cargo"))]++;
    }
  }

  // Warehouses. Their own bucket rather than folded into "in your pieces":
  // a store is not a piece, and material in one is deliberately *parked* — the
  // three buckets stay separate so that what you can spend, what is on the way
  // and what is banked are three answers rather than one.
  for (const sq of structureSquares(state.industry)) {
    const structure = structureAt(state.industry, sq)!;
    if (structure.owner !== side) continue;
    totals.stores++;
    for (const unit of structure.stock) totals.stored[unitSlot(unit)]++;
  }

  return totals;
}

/** Everything in one number, for the one-line summary. */
export const totalUnits = (totals: ShelfTotals): number =>
  totals.held.reduce((a, b) => a + b, 0) + totals.onBelts.reduce((a, b) => a + b, 0);
